import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const FEATURED_SCREEN_CODE_STORAGE_KEY = 'featuredScreenCode';

function SliderView() {
    const params = useParams();
    const routeScreenCode = String(params.id || '').trim();
    const storedScreenCode = (() => {
        try {
            return String(window.localStorage.getItem(FEATURED_SCREEN_CODE_STORAGE_KEY) || '').trim();
        } catch (error) {
            return '';
        }
    })();
    const initialScreenCode = routeScreenCode || storedScreenCode;
    const [screenCodeInput, setScreenCodeInput] = useState(initialScreenCode);
    const [activeScreenCode, setActiveScreenCode] = useState(initialScreenCode);
    const [slides, setSlides] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('Entrez un code écran pour afficher les slides.');

    useEffect(() => {
        if (routeScreenCode) {
            setScreenCodeInput(routeScreenCode);
            setActiveScreenCode(routeScreenCode);
        }
    }, [routeScreenCode]);

    useEffect(() => {
        if (!activeScreenCode) {
            try {
                window.localStorage.removeItem(FEATURED_SCREEN_CODE_STORAGE_KEY);
            } catch (error) {
                // Ignore localStorage errors
            }
            return;
        }

        try {
            window.localStorage.setItem(FEATURED_SCREEN_CODE_STORAGE_KEY, activeScreenCode);
        } catch (error) {
            // Ignore localStorage errors
        }
    }, [activeScreenCode]);

    useEffect(() => {
        let intervalId;

        const fetchSlides = async () => {
            if (!activeScreenCode) {
                setSlides([]);
                setMessage('Entrez un code écran pour afficher les slides.');
                return;
            }

            setIsLoading(true);

            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_PEGASUS_API_URL}/slides/screen/${encodeURIComponent(activeScreenCode)}`
                );

                const nextSlides = response?.data?.response || [];
                const nextMessage = response?.data?.message;

                setSlides(nextSlides);

                if (nextSlides.length > 0) {
                    setMessage('');
                } else {
                    setMessage(nextMessage || 'Aucun slide actif trouve pour ce code ecran.');
                }
            } catch (error) {
                setSlides([]);
                setMessage(
                    error?.response?.data?.response ||
                    error?.response?.data?.message ||
                    'Impossible de charger les slides pour ce code ecran.'
                );
            } finally {
                setIsLoading(false);
            }
        };

        fetchSlides();
        intervalId = window.setInterval(fetchSlides, 7200000);

        return () => {
            if (intervalId) {
                window.clearInterval(intervalId);
            }
        };
    }, [activeScreenCode]);

    useEffect(() => {
        try {
            if (slides.length > 0 && window.bootstrap) {
                const el = document.getElementById('carouselExampleCaptions');
                if (el) {
                    window.bootstrap.Carousel.getInstance(el) ||
                        new window.bootstrap.Carousel(el, {
                            interval: 9000,
                            ride: 'carousel',
                            pause: false,
                            wrap: true,
                        });
                }
            }
        } catch (error) {
            // Ignore bootstrap initialization errors
        }
    }, [slides]);

    const handleSubmit = (event) => {
        event.preventDefault();
        const nextCode = screenCodeInput.trim();
        setActiveScreenCode(nextCode);
    };

    const hasSlides = slides.length > 0;

    return (
        <div className="position-relative bg-dark" style={{ minHeight: '100vh' }}>
            {hasSlides ? (
                <div
                    id="carouselExampleCaptions"
                    className="carousel slide"
                    data-bs-ride="carousel"
                    data-bs-interval="9000"
                    data-bs-pause="false"
                    data-bs-wrap="true"
                    style={{ height: '100vh' }}
                >
                    <div className="carousel-indicators">
                        {slides.map((_, i) => (
                            <button
                                key={`indicator-${i}`}
                                type="button"
                                data-bs-target="#carouselExampleCaptions"
                                data-bs-slide-to={i}
                                className={i === 0 ? 'active' : ''}
                                aria-current={i === 0 ? 'true' : undefined}
                                aria-label={`Slide ${i + 1}`}
                            ></button>
                        ))}
                    </div>

                    <div className="carousel-inner" style={{ height: '100%' }}>
                        {slides.map((slide, i) => (
                            <div
                                key={slide._id || i}
                                className={`carousel-item ${i === 0 ? 'active' : ''}`}
                                style={{ height: '100%' }}
                            >
                                <img
                                    src={`${process.env.REACT_APP_DATA_PIC_PATH}/${slide.url}`}
                                    className="d-block w-100"
                                    alt={`Slide ${i + 1}`}
                                    style={{ objectFit: 'cover', height: '100%' }}
                                />
                            </div>
                        ))}
                    </div>

                    <button
                        className="carousel-control-prev"
                        type="button"
                        data-bs-target="#carouselExampleCaptions"
                        data-bs-slide="prev"
                    >
                        <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                        <span className="visually-hidden">Precedent</span>
                    </button>
                    <button
                        className="carousel-control-next"
                        type="button"
                        data-bs-target="#carouselExampleCaptions"
                        data-bs-slide="next"
                    >
                        <span className="carousel-control-next-icon" aria-hidden="true"></span>
                        <span className="visually-hidden">Suivant</span>
                    </button>
                </div>
            ) : (
                <div
                    className="d-flex align-items-center justify-content-center text-center text-white px-3"
                    style={{ minHeight: '100vh' }}
                >
                    <div
                        className="p-4 rounded shadow"
                        style={{
                            width: 'min(460px, 100%)',
                            backgroundColor: 'rgba(255, 255, 255, 0.94)',
                            color: '#212529',
                        }}
                    >
                        <form onSubmit={handleSubmit}>
                            <div className="fs-3 fw-semibold mb-2">Featured</div>
                            <div className="text-muted mb-4">
                                Saisissez le screen code pour charger les slides associes.
                            </div>
                            <label htmlFor="screenCodeInput" className="form-label fw-semibold mb-2">
                                Code ecran
                            </label>
                            <div className="input-group input-group-lg">
                                <input
                                    id="screenCodeInput"
                                    type="text"
                                    className="form-control"
                                    placeholder="Entrez le screen code"
                                    value={screenCodeInput}
                                    onChange={(event) => setScreenCodeInput(event.target.value)}
                                />
                                <button type="submit" className="btn btn-primary">
                                    Confirmer
                                </button>
                            </div>
                        </form>
                        {activeScreenCode ? (
                            <div className="small text-muted mt-3">
                                Code actif: <strong>{activeScreenCode}</strong>
                            </div>
                        ) : null}
                        <div className="mt-4">
                            {isLoading ? (
                                <div className="fs-5">Chargement des slides...</div>
                            ) : (
                                <>
                                    <div className="fs-5 mb-2">Aucun slide a afficher</div>
                                    <div className="text-muted">{message}</div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SliderView;
